import { parsePhoneNumberFromString } from "libphonenumber-js";

export const AREA_VALUES = ["lt200", "200_500", "500_1000", "gt1000", "undecided"] as const;
export type LeadArea = (typeof AREA_VALUES)[number];

/** The Arabic text of each band. It is the CRM `wanted_area` value, not only the page label. */
export const AREA_LABELS: Record<LeadArea, string> = {
  lt200: "أقل من 200 م²",
  "200_500": "من 200-500 م²",
  "500_1000": "من 500-1,000 م²",
  gt1000: "أكثر من 1,000 م²",
  undecided: "لم أحدد بعد",
};

export const LIMITS = {
  fullNameMin: 2,
  fullNameMax: 200,
  phoneMax: 40,
  emailMax: 200,
  companyMax: 200,
  notesMax: 500,
} as const;

export type LeadField = "fullName" | "phone" | "email" | "company" | "area" | "notes";

export type LeadErrorCode =
  | "required"
  | "too_short"
  | "too_long"
  | "invalid_phone"
  | "invalid_email"
  | "invalid_area";

export interface LeadInput {
  fullName: string;
  phone: string;
  email: string;
  company: string;
  area: string;
  notes: string;
}

export interface LeadValues {
  fullName: string;
  phoneE164: string;
  phoneRaw: string;
  email: string;
  company: string;
  area: LeadArea | "";
  notes: string;
}

export type LeadValidation =
  | { ok: true; value: LeadValues }
  | { ok: false; errors: Partial<Record<LeadField, LeadErrorCode>> };

const ARABIC_INDIC = "٠١٢٣٤٥٦٧٨٩";
const EXTENDED_ARABIC_INDIC = "۰۱۲۳۴۵۶۷۸۹";

export function normalizeDigits(input: string): string {
  let out = "";
  for (const char of input) {
    const arabic = ARABIC_INDIC.indexOf(char);
    if (arabic >= 0) {
      out += String(arabic);
      continue;
    }
    const extended = EXTENDED_ARABIC_INDIC.indexOf(char);
    out += extended >= 0 ? String(extended) : char;
  }
  return out;
}

export function normalizePhone(raw: string): string | null {
  const cleaned = normalizeDigits(raw)
    .replace(/[\s\-().]/g, "")
    .replace(/^00/, "+");
  if (cleaned === "") return null;
  const parsed = parsePhoneNumberFromString(cleaned, "SA");
  if (!parsed || !parsed.isValid() || parsed.country !== "SA") return null;
  if (!parsed.nationalNumber.startsWith("5")) return null;
  return parsed.number;
}

export function isLeadArea(value: string): value is LeadArea {
  return (AREA_VALUES as readonly string[]).includes(value);
}

/**
 * Full name and phone are the only required fields. Email, company, notes and the area band are
 * optional; a filled one is still checked, and the returned value keeps the empty string when it
 * was left blank so the payload can omit it.
 */
export function validateLead(input: LeadInput): LeadValidation {
  const errors: Partial<Record<LeadField, LeadErrorCode>> = {};

  const fullName = input.fullName.trim().replace(/\s+/g, " ");
  if (fullName === "") errors.fullName = "required";
  else if (fullName.length < LIMITS.fullNameMin) errors.fullName = "too_short";
  else if (fullName.length > LIMITS.fullNameMax) errors.fullName = "too_long";

  const phoneRaw = input.phone.trim();
  const phoneE164 = normalizePhone(phoneRaw);
  if (phoneRaw === "") errors.phone = "required";
  else if (phoneRaw.length > LIMITS.phoneMax) errors.phone = "too_long";
  else if (!phoneE164) errors.phone = "invalid_phone";

  const email = input.email.trim();
  if (email.length > LIMITS.emailMax) errors.email = "too_long";
  else if (email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
    errors.email = "invalid_email";
  }

  const company = input.company.trim();
  if (company.length > LIMITS.companyMax) errors.company = "too_long";

  const area = input.area.trim();
  if (area !== "" && !isLeadArea(area)) errors.area = "invalid_area";

  const notes = input.notes.trim();
  if (notes.length > LIMITS.notesMax) errors.notes = "too_long";

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    value: {
      fullName,
      phoneE164: phoneE164 as string,
      phoneRaw,
      email,
      company,
      area: isLeadArea(area) ? area : "",
      notes,
    },
  };
}
